import { HttpErrorResponse } from "@angular/common/http";
import { FormControl, Validators } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { SiqError } from "@models/app/helpers.model";
import { PopupBtnIcon } from "@models/app/modal-data.model";
import { UserInfo } from "@models/auth/cognito.model";
import { Sleeper } from "@models/sleeper/sleeper.model";
import { Navigate } from "@ngxs/router-plugin";
import { Store } from "@ngxs/store";
import { PopupComponent } from "@shared/components/popup/popup.component";
import { SetRegistrationState } from "@store/auth/auth.actions";
import { ResetTacoState } from "@store/taco/taco.actions";
import { Observable, throwError, timer } from "rxjs";
import { CustomValidators } from "./custom-validators.helper";
import { Regex } from "./regex.helper";

import * as CryptoJS from "crypto-js";
import * as exprEval from 'expr-eval';
import * as moment from "moment";

export class FunctionsHelper {

  static camelize(str: string): string {
    return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
      if (+match === 0) return ""; // or if (/\s+/.test(match)) for white spaces
      return index === 0 ? match.toLowerCase() : match.toUpperCase();
    });
  }

  static booleanify(value: string): boolean {
    const truthy: string[] = [
      'true',
      'True',
      '1'
    ];

    return truthy.includes(value);
  }

  static compareByDate<T>(element1: T, element2: T, prop: string): number {
    if (moment((element2 as Record<string, string>)[prop]).isBefore((element1 as Record<string, string>)[prop])) {
      return -1;
    }
    if (moment((element1 as Record<string, string>)[prop]).isBefore((element2 as Record<string, string>)[prop])) {
      return 1;
    }
    return 0;
  }

  static sortBy<T>(col: Array<T>, iterator: (obj: T) => Array<any> | boolean | string): Array<T> {
    const isString = typeof iterator === 'string';
    const len = col.length;
    let temp;
    let lowest;

    const it = JSON.parse(JSON.stringify(col));
    // @ts-expect-error expect error since this is generic function and prop is not used
    it.forEach((_, index: number) => {
      lowest = index;
      if (isString) {
        for (let i = index + 1; i < len; i++) {
          if (it[i][iterator] < it[lowest][iterator]) {
            lowest = i;
          }
        }
      } else {
        for (let i = index + 1; i < len; i++) {
          if (iterator(it[i]) < iterator(it[lowest]) || it[lowest] === undefined) {
            lowest = i;
          }
        }
      }
      if (index !== lowest) {
        temp = it[index];
        it[index] = it[lowest];
        it[lowest] = temp;
      }
    });
    col = it;
    return col;
  }

  static groupBy<T>(arr: Array<T>, key: string): object {
    const initialValue = {};
    return arr.reduce((acc, cval) => {
      const myAttribute = (cval as Record<string, any>)[key];
      (acc as Record<string, any>)[myAttribute] = [...((acc as Record<string, any>)[myAttribute] || []), cval];
      return acc;
    }, initialValue);
  }

  static createGenericError(statusCode: number, message?: string): HttpErrorResponse {
    const errorMsg = message ? message : 'An error has occurred';
    return new HttpErrorResponse({ error: { Error: { Code: statusCode, Message: errorMsg } }, status: statusCode, statusText: 'An error has occurred' });
  }

  // should be called on 400 error (TooManyRequests) and 500 errors
  // should not apply to DELETE (SignOut)
  static genericRetryStrategy(scalingDuration = 1000): (error: HttpErrorResponse) => Observable<HttpErrorResponse | number> {
    return (error: HttpErrorResponse) => {
      if (!this.shouldRetry(error)) {
        return throwError(() => error);
      }
      return timer(scalingDuration);
    };
  }

  static decrypt<T>(payload: string): T {
    if (typeof payload !== 'string') {
      return payload;
    }
    try {
      return JSON.parse(CryptoJS.AES.decrypt(payload, 'selectcomfort').toString(CryptoJS.enc.Utf8));
    } catch (e) {
      return payload as T;
    }
  }

  static encrypt(payload: unknown): string {
    return CryptoJS.AES.encrypt(JSON.stringify(payload), 'selectcomfort').toString();
  }

  static compareByProp(element1: Sleeper, element2: Sleeper, prop: keyof Sleeper): number {
    const value1 = element1[prop];
    const value2 = element2[prop];
    
    if(value1 && value2) {
      if (value1 < value2) {
        return 1;
      } else if (value2 < value1) {
        return -1;
      }
    }
    return 0;
  }

  static createSiqError(code: number, message: string): SiqError {
    return new SiqError({ code, message });
  }

  static addTrademarkToText(text: string): string {
    let tip = text;
    if (tip.includes('®')) {
      tip = tip.replace('®', '');
    }
    if (text.includes('SleepIQ')) {
      tip = tip.replace(/SleepIQ/g, 'SleepIQ<sup>\u00AE</sup>');
    }
    if (text.includes('Sleep Number')) {
      tip = tip.replace(/Sleep Number/g, 'Sleep Number<sup>\u00AE</sup>');
    }
    return tip;
  }

  static scrollToTop(): void {
    if (self.innerWidth < 600) {
      window.scrollTo(0, 0);
    } else {
      const middleColumn = document.getElementsByClassName('middle-column')[0];
      middleColumn.scrollTop = 0;
    }
  }

  static indexBy<T>(arr: Array<T>, key: string): any {
    const grouped = this.groupBy(arr, key);
    const indexed = { ...grouped };
    for (const [k, value] of Object.entries(indexed)) {
      if (value && (value as Record<string, any>)[0]) {
        (indexed as Record<string, any>)[k] = (value as Record<string, any>)[0];
      } else {
        delete (indexed as Record<string, any>)[k];
      }
    }
    return indexed;
  }

  static evaluateExpression(expression: string, x: string): number {
    const parser = new exprEval.Parser();
    const expr = parser.parse(expression);
    const scope = { x };
    return expr.evaluate(scope);
  }

  // transform string WORD-TWO to Word-Two
  // example: KING-SPLIT -> King-Split
  static transformBedSizeString(input: string): string {
    // Split the input string by '-' character
    const parts = input.split('-');

    // Capitalize the first letter of each part and convert the rest to lowercase
    const transformedParts = parts.map(part => FunctionsHelper.capitalizeFirstLetter(part));

    // Join the parts back together with '-' in between
    const transformedString = transformedParts.join('-');

    return transformedString;
  }

  static capitalizeFirstLetter(input: string): string {
    return `${input.charAt(0).toUpperCase()}${input.slice(1).toLowerCase()}`;
  }

  static transformArrayToString(input: Array<string>, isT360: boolean): string {
    const [firstString, ...restStrings] = input;
    let resultString = '';
    if (isT360) {
      resultString = input.join(' ');
    } else {
      if (restStrings.length > 0) {
        resultString = `${firstString}, ${restStrings.join(' ')}`;
      } else {
        resultString = firstString;
      }
    }
    return resultString;
  }

  static decodeJWT(token: string): Record<string, any> {
    const payload = token.split('.')[1];
    const wordArray = CryptoJS.enc.Base64.parse(payload);
    const str = CryptoJS.enc.Utf8.stringify(wordArray);
    return JSON.parse(str);
  }

  static handleUserNavigation(store: Store, userInfo: UserInfo): void {
    if (userInfo.registrationState === 0) {
      store.dispatch(new SetRegistrationState(10));
      store.dispatch(new Navigate(['pages/setup/select-bed']));
    } else if (userInfo.registrationState === 10) {
      store.dispatch(new Navigate(['pages/setup/select-bed']));
    } else {
      store.dispatch(new Navigate(['pages/sleep']));
    }
    store.dispatch(new ResetTacoState());
  }

  static get passwordFormControl(): FormControl {
    return new FormControl('', Validators.compose([
      // 1. Required
      Validators.required,
      // 2. check whether the entered password has a number
      CustomValidators.patternValidator(Regex.number, { hasNumber: true }),
      // 3. check whether the entered password has upper case letter
      CustomValidators.patternValidator(Regex.capitalCase, { hasCapitalCase: true }),
      // 4. check whether the entered password has upper case letter
      CustomValidators.patternValidator(Regex.lowerCase, { hasLowerCase: true }),
      // 5. check whether the entered password has a special character
      CustomValidators.patternValidator(Regex.specialCharacter, { hasSpecialCharacters: true }),
      // 6. check whether the entered password has a white space
      CustomValidators.patternValidator(Regex.whitespace, { hasWhitespace: true }),
      // 7. Has a minimum length of 8 characters
      Validators.minLength(8),
      // 8. Has maximum length of 99 characters
      Validators.maxLength(99),
    ]));
  }

  // use siqpopup helper
  static createPopup(dialog: MatDialog, title: string, text: string, screen: string, icon: string, type: string, rightBtnTxt: string, leftBtnTxt?: string | null, leftBtnIcon?: PopupBtnIcon | null, rightBtnIcon?: PopupBtnIcon | null): MatDialogRef<PopupComponent> {
    return dialog.open(PopupComponent, {
      width: '327px',
      height: 'auto',
      disableClose: false,
      data: {
        screen,
        icon,
        title,
        text,
        hideLeftButton: leftBtnTxt ? false : true,
        rightBtnTxt,
        leftBtnTxt,
        leftBtnIcon,
        rightBtnIcon,
        type
      }
    });
  }

  static removeHtmlTags(input: string): string {
    return input.replace(/<[^>]*>/g, '');
  }

  //#region Private helper functions
  private static shouldRetry(error: HttpErrorResponse): boolean {
    return (error.status === 400 && error.error.data.code === 'TooManyRequestsException') || (error.status >= 500) || !navigator.onLine;
  }
  //#endregion
}